import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, lastValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

import { StackedNotificationService } from './stacked-notifications.service';
import { StbyCreateNotificationArgs } from './types';
import { ModalService } from '../../shared/modal';

import { ButtonType } from 'app/shared/buttons';
import { IconType } from 'app/shared/icon';

@Injectable()
export class NotificationsService {
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(ModalService);
    private readonly router = inject(Router);
    private readonly stackedNotificationService = inject(StackedNotificationService);

    private readonly successIcon = 'alert-checked-filled';
    private readonly successIconFill = 'var(--state-success)';
    private readonly errorIcon = 'alert-error-filled';
    private readonly errorIconFill = 'var(--state-error)';
    private readonly warningIcon = 'alert-warning-filled';
    private readonly warningIconFill = 'var(--state-warning)';
    private readonly infoIcon = 'alert-warning-filled';
    private readonly infoIconFill = 'var(--state-info)';
    private modalOpened: boolean;

    error(title: string, content?: string, id?: string): void {
        this.create({
            title,
            content,
            icon: this.errorIcon,
            iconFill: this.errorIconFill,
            id,
        });
    }

    info(title: string, content?: string, id?: string): void {
        this.create({
            title,
            content,
            icon: this.infoIcon,
            iconFill: this.infoIconFill,
            id,
        });
    }

    success(title: string, content?: string, id?: string): void {
        this.create({
            title,
            content,
            icon: this.successIcon,
            iconFill: this.successIconFill,
            id,
        });
    }

    warning(title: string, content?: string, id?: string): void {
        this.create({
            title,
            content,
            icon: this.warningIcon,
            iconFill: this.warningIconFill,
            id,
        });
    }

    translateError(title: string, content?: string, id?: string): void {
        this.createTranslation({
            title,
            content,
            icon: this.errorIcon,
            iconFill: this.errorIconFill,
            id,
        });
    }

    translateInfo(title: string, content?: string, id?: string): void {
        this.createTranslation({
            title,
            content,
            icon: this.infoIcon,
            iconFill: this.infoIconFill,
            id,
        });
    }

    translateSuccess(title: string, content?: string, id?: string): void {
        this.createTranslation({
            title,
            content,
            icon: this.successIcon,
            iconFill: this.successIconFill,
            id,
        });
    }

    translateWarning(title: string, content?: string, id?: string): void {
        this.createTranslation({
            title,
            content,
            icon: this.warningIcon,
            iconFill: this.warningIconFill,
            id,
        });
    }

    async translateTypeMismatchModal(): Promise<void> {
        // No error handler needed: getModalOpenObservable() is an internal UI-state
        // stream, not a network call, and does not error.
        this.modalService
            .getModalOpenObservable()
            .pipe(first())
            .subscribe((response) => (this.modalOpened = response));

        if (!this.modalOpened) {
            const navigateAndReload = () => this.router.navigate(['/me/home']).then(() => window.location.reload());
            const reloadWithDelay = setTimeout(() => navigateAndReload(), 10000);

            this.modalService.createModal({
                title: 'error.type_mismatch.title',
                contentItems: [
                    {
                        contentTranslationKey: 'error.type_mismatch.description',
                    },
                ],
                buttons: [
                    {
                        label: 'global.cancel',
                        type: ButtonType.text,
                        callback: () => {
                            clearTimeout(reloadWithDelay);
                            this.modalService.closeModal();
                        },
                    },
                    {
                        label: 'global.refresh',
                        callback: () => {
                            clearTimeout(reloadWithDelay);
                            navigateAndReload();
                        },
                    },
                ],
            });
        }
    }

    private create({ content, title, icon, iconFill, id }: StbyCreateNotificationArgs): void {
        this.stackedNotificationService.show({
            title,
            content,
            icon: icon as IconType,
            iconFill,
            duration: 5000,
            id,
        });
    }

    private createTranslation(notification: {
        title: string;
        content: string;
        icon: string;
        iconFill?: string;
        id?: string;
    }): void {
        if (notification.content) {
            lastValueFrom(
                combineLatest([
                    this.translateService.get(notification.title),
                    this.translateService.get(notification.content),
                ]),
            ).then((results: string[]) => {
                notification.title = results[0];
                notification.content = results[1];
                this.create(notification);
            });
        } else {
            lastValueFrom(this.translateService.get(notification.title)).then((result: string) => {
                notification.title = result;
                this.create(notification);
            });
        }
    }
}
